import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import pq from 'pqgrid';
import { debounceTime } from 'rxjs';
import { Customer } from '../models/Customer';
import { DispatchItem } from '../models/DispatchItem';
import { FormStatus } from '../models/Enums';
import { ErrorState, RoundFormState } from '../models/FormState';
import { ListItem } from '../models/ListItem';
import { Round } from '../models/Round';
import { RoundParameters } from '../models/RoundParameters';
import { PqgridComponent } from '../pqgrid/pqgrid.component';
import { ListDataService } from '../services/list-data.service';
import { RoundsDataService } from '../services/rounds-data.service';
import { RoundCompleteEvent } from '../models/RoundCompleteEvent';

@Component({
    selector: 'round-form',
    templateUrl: 'round-form.component.html',
    styles: [],
    standalone: false
})
export class RoundFormComponent implements OnInit, OnChanges, AfterViewInit {

    constructor(private _listDataService: ListDataService, private _roundDataService: RoundsDataService) { }

    @ViewChild('dispatchGrid') dispatchGrid: PqgridComponent;
    @ViewChild('closeButton') closeButton;
    @ViewChild('roundModalDiv') roundModal;
    @ViewChild('truckerSearchDropdown') truckerSearchDropdownRef: ElementRef;
    private observer: IntersectionObserver;

    @Input() parameters: RoundParameters = new RoundParameters();
    @Input() roundFormId: string = 'roundModal'
    @Output() roundComplete: EventEmitter<RoundCompleteEvent> = new EventEmitter<RoundCompleteEvent>();

    errorMessage: string = '';
    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;
    formState: RoundFormState = new RoundFormState()
    isNew: boolean = false;
    clearGridFilters: boolean = false;

    roundParameters: RoundParameters = new RoundParameters();
    locList: ListItem[] = [];
    round: Round = new Round();

    dispatchColumns = [
        {
            title: "", width: 30, type: "checkbox", dataIndx: "selected", resizable: false, editable: true, align: 'center',
            sortable: false, nodrag: true, dataType: "bool", editor: false
        },
        { title: "Loc", width: 70, dataType: "string", dataIndx: "locCode", editable: false, hidden: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Origin Customer", width: 180, dataType: "string", dataIndx: "customerName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Origin", width: 150, dataType: "string", dataIndx: "originName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Origin City", width: 120, dataType: "string", dataIndx: "originCity", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Dest Customer", width: 180, dataType: "string", dataIndx: "destinationCustomerName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Destination", width: 150, dataType: "string", dataIndx: "destinationName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Dest City", width: 120, dataType: "string", dataIndx: "destinationCity", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Comm", width: 70, dataType: "string", dataIndx: "commodityCode", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Loads", width: 70, dataType: "integer", dataIndx: "loads", editable: false, filter: { crules: [{ condition: "equal" }] } },
        { title: "Trucker", width: 170, dataType: "string", dataIndx: "truckerName", editable: false, filter: { crules: [{ condition: "contain" }] } },
        { title: "Rate", width: 100, dataType: "string", dataIndx: "rate", editable: false, filter: { crules: [{ condition: "contain" }] } }
    ];

    gridOptions: pq.gridT.options = {
        menuIcon: false,
        showTop: false,
        roundCorners: true,
        reactive: true,
        locale: "en",
        height: "300",
        fillHandle: "",
        freezeCols: 1,
        resizable: true,
        numberCell: {
            show: false
        },
        colModel: this.dispatchColumns,
        animModel: {
            on: true
        },
        dataModel: {
            data: []
        },
        sortModel: {
            cancel: true,
            type: "local",
            single: false,
            space: false
        },
        filterModel: {
            header: true,
            menuIcon: false
        },
        postRenderInterval: -1,
        wrap: false        
    };

    clearFilters() {
        this.dispatchGrid.grid.reset({ filter: true });
    }

    clearSorting() {
        this.dispatchGrid.grid.reset({ sort: true });
    }

    dispatchSelected: boolean = false;
    selectedDispatch: DispatchItem = new DispatchItem();
    
    selectDispatch(dispatch: DispatchItem) {
        let selecting = !dispatch.selected;
        if (selecting) {
            this.addDispatchError = '';
            this.selectedDispatch = dispatch;
            this.dispatchSelected = true;
            this.addDispatchLoads = dispatch.numberOfLoads;
            this.unselectDispatches(dispatch.dispatchId);                        
        }
        else {
            this.clearSelectedDispatch();
        }
    }

    unselectDispatches(dispatchId: number) {
        let dispatches: DispatchItem[] = this.gridOptions.dataModel.data as DispatchItem[];
        let index: number = 0;
        for (let item of dispatches) {
            if (item.dispatchId !== dispatchId) {
                if (item.selected) {
                    item.selected = false;
                    this.dispatchGrid.grid.refreshRow({ rowIndx: index });
                }
            }
            index++;
        }
    }

    addDispatchLoads: number = 0;
    addDispatchError: string = '';

    clearSelectedDispatch() {
        this.dispatchSelected = false;
        this.selectedDispatch = new DispatchItem();
        this.addDispatchLoads = 0;
        this.addDispatchError = '';
    }

    addDispatch(): boolean {
        this.addDispatchError = ''
        if (this.addDispatchLoads <= 0) {
            this.addDispatchError = 'Loads to add must be greater than 0';
            return false;
        }
        if (this.addDispatchLoads > this.selectedDispatch.numberOfLoads) {
            this.addDispatchError = 'Loads to add must be equal to or less than the selected dispatch loads of ' + this.selectedDispatch.loads;
            return false;
        }

        this.selectedDispatch.selected = false;

        //add dispatch to round
        let roundDispatch: DispatchItem = { ...this.selectedDispatch };
        roundDispatch.numberOfLoads = this.addDispatchLoads;
        roundDispatch.loads = this.addDispatchLoads.toString();
        this.round.dispatches.push(roundDispatch);

        //set round trucker if not already set
        if (this.round.truckerId == 0 && roundDispatch.truckerId > 0) {
            this.round.truckerId = roundDispatch.truckerId;
            this.round.truckerName = roundDispatch.truckerName;
            this.validateTrucker();
        }

        //remove from dispatch grid or change loads if didn't use all loads
        let dispatches: DispatchItem[] = this.gridOptions.dataModel.data as DispatchItem[];
        let index: number = dispatches.findIndex(d => d.dispatchId == roundDispatch.dispatchId);
        if (index > -1) {
            let dispatchLoads: number = this.selectedDispatch.numberOfLoads - roundDispatch.numberOfLoads;
            if (dispatchLoads > 0) {
                this.selectedDispatch.numberOfLoads = dispatchLoads;
                this.selectedDispatch.loads = dispatchLoads.toString();
            }
            else {
                this.gridOptions.dataModel.data.splice(index, 1);
            }
            //this.dispatchGrid.grid.refreshRow({ rowIndx: index });
        }

        this.clearSelectedDispatch();

        return true;
    }

    removeDispatch (index: number, dispatch: DispatchItem) {
        this.round.dispatches.splice(index, 1);

        if (this.roundParameters.locCode == dispatch.locCode) {
            //see if dispatch already exists in grid
            let dispatches: DispatchItem[] = this.gridOptions.dataModel.data as DispatchItem[];
            let index: number = dispatches.findIndex(d => d.dispatchId == dispatch.dispatchId);
            if (index > -1) {
                //add loads back to dispatch in grid
                //should probably retrieve dispatch from database again here - esp someone else could update it
                //could get all dispatches again but then ones that have been added to round but not saved yet would show again
                let gridDispatch = this.gridOptions.dataModel.data[index] as DispatchItem;
                gridDispatch.numberOfLoads += dispatch.numberOfLoads;
                gridDispatch.loads = gridDispatch.numberOfLoads.toString();
                this.dispatchGrid.grid.refreshRow({ rowIndx: index });
            }
            else {
                //add dispatch back to grid
                this.gridOptions.dataModel.data.push(dispatch);
                this.dispatchGrid.grid.refreshDataAndView();
            }
        }
        return false;
    }

    moveOrderUp(index: number, dispatch: DispatchItem) {
        if (index > 0) {
            let switchDispatch = this.round.dispatches[index - 1];
            this.round.dispatches[index - 1] = dispatch;
            this.round.dispatches[index] = switchDispatch;
        }
        return false;
    }

    moveOrderDown(index: number, dispatch: DispatchItem) {
        if (index < this.round.dispatches.length - 1) {
            let switchDispatch = this.round.dispatches[index + 1];
            this.round.dispatches[index + 1] = dispatch;
            this.round.dispatches[index] = switchDispatch;
        }
        return false;
    }

    truckerList: Customer[] = [];
    truckerSearchForm = this.createTruckerSearchForm();
    showTruckerSearch: boolean = false;

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const isTruckerSearchDropdown = this.truckerSearchDropdownRef?.nativeElement?.contains(target);

        if (!isTruckerSearchDropdown) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        }
    }

    toggleTruckerSearch(): void {
        if (this.showTruckerSearch) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        } else {
            this.showTruckerSearch = true;
        }
    }

    createTruckerSearchForm() {
        const truckerSearchForm = new FormGroup({
            customerName: new FormControl(''),
            city: new FormControl(''),
            customerCode: new FormControl(''),
            isTrucker: new FormControl(true),
            customerId: new FormControl(0),
            preferredCurrency: new FormControl(''),
            hasCompliance: new FormControl(true),
            isCompliant: new FormControl(true)
        });

        truckerSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchTruckers();
            });
        return truckerSearchForm;
    }

    searchTruckers() {
        if (this.truckerSearchForm.controls.customerName.value.length > 1 ||
            this.truckerSearchForm.controls.city.value.length > 1 ||
            this.truckerSearchForm.controls.customerCode.value.length > 1) {
            this._listDataService.searchCustomers(this.truckerSearchForm.getRawValue()).subscribe({
                next: data => {
                    this.truckerList = data;
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.truckerList = [];
        }
    }

    clearTruckerSearch() {
        this.truckerSearchForm = this.createTruckerSearchForm();
        this.truckerList = [];
    }

    selectTrucker(trucker: Customer): void {
        this.round.truckerId = trucker.customerId;
        this.round.truckerName = trucker.customerName;
        this.validateTrucker();
        this.showTruckerSearch = false;
    }

    validateDispatches() {
        this.addDispatchError = '';
        this.formState.dispatchCount = new ErrorState();
        if (this.formState.submitted) {
            if (this.round.dispatches.length < 2) {
                this.formState.dispatchCount.invalid = true;
                this.addDispatchError = 'At least 2 dispatches must be added to the round';
            }
        }
    }

    validateTrucker() {
        this.formState.trucker = new ErrorState();
        if (this.formState.submitted) {
            if (this.round.truckerId == 0) {
                this.formState.trucker.invalid = true;
                this.formState.trucker.errorMessage = 'Trucker is required';
            }
        }
    }

    validateDispatchTrucker() {
        this.formState.dispatchTrucker= new ErrorState();
        if (this.formState.submitted) {
            let dispatches = this.round.dispatches.filter(x => x.truckerId != this.round.truckerId);
            if (dispatches.length > 0) {
                this.formState.dispatchTrucker.invalid = true;
                this.formState.dispatchTrucker.errorMessage = 'The round contains dispatches with other truckers that will be reassigned to ' + this.round.truckerName + '.  Confirm Save to continue.';
            }
        }
    }
    
    validateForm() {
        this.validateDispatches();
        this.validateTrucker();
        this.validateDispatchTrucker();
    }

    saveRound(saveConfirmed: boolean): void {
        this.formState.submitted = true;
        this.validateForm();

        if (!this.formState.isValid()) {
            this.formStatus = FormStatus.Normal;
            return;
        }
        if (!saveConfirmed && this.formState.dispatchTrucker.invalid) {
            this.formStatus = FormStatus.ConfirmSave;
            return;
        }
      
        this.formStatus = FormStatus.Saving;
        this._roundDataService.saveRound(this.round).subscribe({
            next: data => {
                let eventParms = new RoundCompleteEvent();
                eventParms.scheduleIds = data;
                eventParms.message = "Round was saved successfully";
                this.roundComplete.emit(eventParms);
                this.closeButton.nativeElement.click();
            },
            error: err => {
                this.errorMessage = err;
                this.formStatus = FormStatus.Normal;
            }
        });
    }

    cancelSave() {
        this.formStatus = FormStatus.Normal;
    }

    deleteRound(): void {
        this.formStatus = FormStatus.Delete;
    }
    
    cancelDelete(): void {
        this.formStatus = FormStatus.Normal;
    }

    confirmDelete(): void {
        this.formStatus = FormStatus.Deleting;
        this._roundDataService.deleteRound(this.round.roundId).subscribe({
            next: data => {
                let eventParms = new RoundCompleteEvent();
                eventParms.scheduleIds = data;
                eventParms.message = "Round was deleted";
                this.roundComplete.emit(eventParms);
                this.closeButton.nativeElement.click();
            },
            error: err => {
                this.errorMessage = err;
                this.formStatus = FormStatus.Normal;
            }
        });
    }
   
    getDispatches() {
        //console.log(JSON.stringify(this.roundParameters));
        if (this.roundParameters.locCode.length === 0) {
            return;
        }

        this._roundDataService.getDispatches(this.roundParameters).subscribe({
            next: data => {
                this.gridOptions.dataModel.data = data;
            },
            error: err => this.errorMessage = err
        });
    }

    defaultSelectDispatch() {
        if (this.roundParameters.dispatchId == 0) {
            return;
        }
        let dispatches = this.gridOptions.dataModel.data as DispatchItem[];
        let dispatchIndex = dispatches.findIndex(d => d.dispatchId == this.roundParameters.dispatchId);

        if (dispatchIndex > -1) {
            let dispatchItem = dispatches[dispatchIndex];
            this.selectDispatch(dispatchItem);
            dispatchItem.selected = true;
            this.dispatchGrid.grid.refreshRow({ rowIndx: dispatchIndex });
            this.dispatchGrid.grid.scrollRow({ rowIndxPage: dispatchIndex });
            //clear dispatchId so this only happens once
            this.roundParameters.dispatchId = 0;
        }
    }

    getRound() {
        if (this.roundParameters.roundId > 0) {
            this._roundDataService.getRound(this.roundParameters.roundId).subscribe({
                next: data => {
                    this.round = data;
                },
                error: err => this.errorMessage = err
            });
            this.isNew = false;
        }
    }

    resetComponent() {
        this.formStatus = FormStatus.Normal;
        this.formState = new RoundFormState();
        //this.parameters = new RoundParameters();
        //this.roundParameters = new RoundParameters();
        this.gridOptions.dataModel.data = [];
        this.round = new Round();
        this.clearSelectedDispatch();   
        this.clearTruckerSearch();
        this.isNew = true;
        this.clearGridFilters = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['parameters']) {
            this.roundParameters = { ...this.parameters };
            this.resetComponent();
            this.getDispatches();
            this.getRound();
        }
    }

    ngOnInit(): void {
        this._listDataService.getListItems("loc").subscribe({
            next: data => {
                this.locList = data;
            },
            error: err => this.errorMessage = err
        });

        let self = this;

        //need to make call to select the default dispatch (user selected dispatch from scheduling screen) after the dispatch grid is done loading
        this.gridOptions.complete = function (event, ui) {
            if (self.clearGridFilters) {
                //this.dispatchGrid.grid.refresh();
                self.clearFilters();
                self.clearSorting();
                self.clearGridFilters = false;
            }
            self.defaultSelectDispatch();
        }
        
        this.gridOptions.colModel[0].postRender = function (ui) {
            var rowData = ui.rowData;
            var $cell = this.getCell(ui);
            var $checkbox = $cell.find("input[type='checkbox']");
            $checkbox.change(function () {
                self.selectDispatch(rowData);
            });
        };
    }

    ngAfterViewInit(): void {
        //mega hack to refresh grid when the modal is visible to fix issue with grid not rendering
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    //console.log('modal is visible');
                    if (this.dispatchGrid) {
                        //console.log('refresh grid');
                        this.dispatchGrid.grid.refresh();
                    }
                }
            });
        });

        this.observer.observe(this.roundModal.nativeElement);
    }

    ngOnDestroy(): void {
        this.observer.disconnect();
    }
    
}
